<template>
  <v-dialog v-model="internalDialog" max-width="1100px" scrollable persistent transition="dialog-bottom-transition">
    <v-card class="rounded-lg overflow-hidden bg-white">
      <!-- HEADER -->
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 success darken-2 white--text">
        <div class="d-flex align-center">
          <v-avatar color="white" size="40" class="me-3">
            <v-icon color="success darken-2" size="24">mdi-ticket-account</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-black white--text mb-0">تقرير إيرادات الحجوزات</div>
            <div class="text-caption white--text text--lighten-3 opacity-90">
              تحليل إجمالي الإيرادات الناتجة عن حجوزات العملاء وتقسيمها حسب الطرق والسائقين
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn
            color="white"
            light
            class="font-weight-bold elevation-2 me-3 text-none green--text text--darken-4"
            :loading="loadingCsv"
            @click="exportCsv"
          >
            <v-icon left color="green darken-4">mdi-file-delimited</v-icon>
            تصدير CSV
          </v-btn>

          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- BODY -->
      <v-card-text class="pa-5 bg-grey-lighten-5">
        <v-alert v-if="errorMessage" type="error" dense class="mb-4 font-weight-bold d-flex align-center justify-space-between">
          <div>{{ errorMessage }}</div>
          <v-btn x-small color="white" text class="font-weight-bold ms-3" @click="fetchReport">إعادة المحاولة</v-btn>
        </v-alert>

        <!-- 1. FILTERS BAR -->
        <v-card outlined class="pa-4 mb-5 rounded-lg border-grey bg-white elevation-1">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">الفترة الزمنية:</div>
              <v-select
                v-model="selectedPeriod"
                :items="periodOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-select>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="4">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">من تاريخ:</div>
              <v-text-field
                v-model="dateFrom"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="4">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">إلى تاريخ:</div>
              <v-text-field
                v-model="dateTo"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card>

        <!-- 2. KPI CARDS -->
        <v-row dense class="mb-5">
          <v-col cols="12" sm="6" md="2.4">
            <v-card outlined class="pa-4 rounded-lg border-primary bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي الإيرادات المحصلة (Gross)</div>
              <div class="text-h5 font-weight-black primary--text mt-1">
                {{ summary.total_revenue || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">قبل الاسترداد</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2.4">
            <v-card outlined class="pa-4 rounded-lg border-warning bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي المبالغ المستردة</div>
              <div class="text-h5 font-weight-black warning--text text--darken-3 mt-1">
                {{ summary.total_refunds_amount || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">المسترد للعملاء</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2.4">
            <v-card outlined class="pa-4 rounded-lg border-success bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">صافي الإيرادات المحتفظ بها</div>
              <div class="text-h5 font-weight-black green--text text--darken-3 mt-1">
                {{ summary.retained_revenue || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">المحصل - المسترد</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2.4">
            <v-card outlined class="pa-4 rounded-lg border-info bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إيرادات النقدي / الإلكتروني</div>
              <div class="text-subtitle-1 font-weight-bold text-dark-primary mt-1">
                {{ summary.cash_revenue || '0' }} / {{ summary.electronic_revenue || '0' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">نقدي (كاش) / محفظة</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2.4">
            <v-card outlined class="pa-4 rounded-lg border-secondary bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">عدد الحجوزات المدفوعة</div>
              <div class="text-h5 font-weight-black text-dark-primary mt-1">
                {{ summary.paid_bookings_count || 0 }} <span class="text-caption font-weight-bold">تذكرة</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">إجمالي التذاكر المدفوعة</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- 3. TOP REVENUE TRIPS TABLE -->
        <v-card outlined class="rounded-lg border-grey bg-white mb-5 elevation-1">
          <v-card-title class="pa-3 grey lighten-4 border-bottom d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-bold text-dark-primary">
              <v-icon size="20" color="success" class="me-1">mdi-trophy-outline</v-icon>
              أعلى الرحلات والمسارات تحقيقاً للإيرادات
            </span>
          </v-card-title>

          <v-data-table
            :headers="tripHeaders"
            :items="topTrips"
            :loading="loading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.gross_revenue="{ item }">
              <span class="font-weight-black green--text text--darken-3">{{ item.gross_revenue }} EGP</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center py-4 text-dark-secondary">لا توجد بيانات رحلات في الفترة المحددة</div>
            </template>
          </v-data-table>
        </v-card>

        <!-- 4. TOP DRIVERS BY REVENUE GENERATED TABLE -->
        <v-card outlined class="rounded-lg border-grey bg-white elevation-1">
          <v-card-title class="pa-3 grey lighten-4 border-bottom d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-bold text-dark-primary">
              <v-icon size="20" color="primary" class="me-1">mdi-account-tie-hat</v-icon>
              أعلى السائقين تحقيقاً للإيرادات (إيرادات الرحلات المشغلة)
            </span>
          </v-card-title>

          <v-data-table
            :headers="driverHeaders"
            :items="topDrivers"
            :loading="loading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.revenue_generated="{ item }">
              <span class="font-weight-black primary--text">{{ item.revenue_generated }} EGP</span>
            </template>
            <template v-slot:no-data>
              <div class="text-center py-4 text-dark-secondary">لا توجد بيانات سائقين في الفترة المحددة</div>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-white d-flex justify-space-between align-center">
        <div class="caption font-weight-bold text-dark-secondary">
          BEEPGO Reporting Center — تقرير إيرادات الحجوزات
        </div>
        <v-btn color="grey darken-2" text class="font-weight-bold" @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'BookingRevenueModal',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      internalDialog: false,
      loading: false,
      loadingCsv: false,
      errorMessage: null,

      selectedPeriod: 'month',
      dateFrom: null,
      dateTo: null,

      periodOptions: [
        { text: 'اليوم (Today)', value: 'today' },
        { text: 'هذا الأسبوع (This Week)', value: 'week' },
        { text: 'هذا الشهر (This Month)', value: 'month' },
        { text: 'هذه السنة (This Year)', value: 'year' },
        { text: 'آخر 7 أيام', value: 'last_7_days' },
        { text: 'آخر 30 يوم', value: 'last_30_days' },
        { text: 'فترة مخصصة (Custom)', value: 'custom' },
        { text: 'الكل (All Time)', value: 'all' },
      ],

      summary: {
        total_revenue: '0.00',
        cash_revenue: '0.00',
        electronic_revenue: '0.00',
        paid_bookings_count: 0,
      },

      topTrips: [],
      topDrivers: [],

      tripHeaders: [
        { text: 'رقم الرحلة', value: 'trip_id' },
        { text: 'اسم المسار', value: 'route_name' },
        { text: 'عدد الحجوزات', value: 'bookings_count' },
        { text: 'عدد الركاب', value: 'passengers_count' },
        { text: 'إجمالي الإيراد', value: 'gross_revenue' },
      ],

      driverHeaders: [
        { text: 'رقم السائق', value: 'driver_id' },
        { text: 'اسم السائق', value: 'driver_name' },
        { text: 'رقم الهاتف', value: 'driver_phone' },
        { text: 'الرحلات المنفذة', value: 'trips_operated' },
        { text: 'عدد الحجوزات', value: 'bookings_count' },
        { text: 'الإيرادات المحققة', value: 'revenue_generated' },
      ],
    };
  },
  watch: {
    value(val) {
      this.internalDialog = val;
      if (val) {
        this.fetchReport();
      }
    },
    internalDialog(val) {
      this.$emit('input', val);
    },
  },
  methods: {
    fetchReport() {
      this.loading = true;
      this.errorMessage = null;

      const params = {
        period: this.selectedPeriod,
      };
      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/booking-revenue', { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data;
            this.summary = d.summary || this.summary;
            this.topTrips = d.top_trips || [];
            this.topDrivers = d.top_drivers || [];
          } else {
            this.errorMessage = res.data.message || 'خطأ في تحميل التقرير';
          }
        })
        .catch((err) => {
          this.errorMessage = 'تعذر الاتصال بالخادم لجلب التقرير';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportCsv() {
      this.loadingCsv = true;
      const params = { period: this.selectedPeriod };
      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/booking-revenue/export-csv', {
          params,
          responseType: 'blob',
        })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `booking-revenue-${moment().format('YYYY-MM-DD')}.csv`;
          link.click();
          window.URL.revokeObjectURL(link.href);
        })
        .catch((err) => {
          console.error('Error exporting CSV:', err);
        })
        .finally(() => {
          this.loadingCsv = false;
        });
    },
    closeModal() {
      this.internalDialog = false;
    },
  },
};
</script>
